import {
    ContactsIntelligenceResponse,
    CustomerIntelligenceResponse,
    EntityIntelligenceResponse,
    SenderProfileResponse,
    OrderDetail,
    OrdersAnalyticsFilters,
    OrdersCustomersResponse,
    OrdersDashboardResponse,
    OrdersDrilldownResponse,
    OrdersFilterOptionsResponse,
    OrdersMonetizationResponse,
    OrdersSegmentationResponse,
    OrdersSummaryResponse,
    OrdersTodayResponse,
    OrdersTrendsResponse,
    SegmentationDimension,
    AudienceSegmentType,
} from "@/features/orders-analytics/types/orders-analytics.types";
import { httpClient } from "@/shared/http/httpClient";

const API_PATH = "/api/v1/analytics/orders";

const DEMO_METRICS_MULTIPLIER = 1;

const inflateValue = (value: number): number => {
    return value * DEMO_METRICS_MULTIPLIER;
};

const inflateMetric = <T extends { current: number; previous: number; delta: number; trend: { value: number }[] }>(
    metric: T
): T => ({
    ...metric,
    current: inflateValue(metric.current),
    previous: inflateValue(metric.previous),
    delta: inflateValue(metric.delta),
    trend: metric.trend.map((point) => ({
        ...point,
        value: inflateValue(point.value),
    })),
});

const inflateSummary = (summary: OrdersSummaryResponse): OrdersSummaryResponse => ({
    ...summary,
    volume: inflateMetric(summary.volume),
    completedOrders: inflateMetric(summary.completedOrders),
    businessRevenue: inflateMetric(summary.businessRevenue),
});

const inflateTrends = (trends: OrdersTrendsResponse): OrdersTrendsResponse => ({
    ...trends,
    points: trends.points.map((point) => ({
        ...point,
        volume: inflateValue(point.volume),
        completedOrders: inflateValue(point.completedOrders),
        businessRevenue: inflateValue(point.businessRevenue),
    })),
    previousPoints: trends.previousPoints?.map((point) => ({
        ...point,
        volume: inflateValue(point.volume),
        completedOrders: inflateValue(point.completedOrders),
        businessRevenue: inflateValue(point.businessRevenue),
    })),
});

const inflateMonetization = (
    monetization: OrdersMonetizationResponse
): OrdersMonetizationResponse => ({
    ...monetization,
    totalBusinessRevenue: inflateValue(monetization.totalBusinessRevenue),
    ticketSizeDistribution: monetization.ticketSizeDistribution.map((bucket) => ({
        ...bucket,
        volume: inflateValue(bucket.volume),
        businessRevenue: inflateValue(bucket.businessRevenue),
    })),
    revenueByOrderSize: monetization.revenueByOrderSize.map((bucket) => ({
        ...bucket,
        volume: inflateValue(bucket.volume),
        businessRevenue: inflateValue(bucket.businessRevenue),
    })),
});

const inflateSegmentation = (
    segmentation: OrdersSegmentationResponse
): OrdersSegmentationResponse => ({
    ...segmentation,
    rows: segmentation.rows.map((row) => ({
        ...row,
        volume: inflateValue(row.volume),
        orders: inflateValue(row.orders),
        businessRevenue: inflateValue(row.businessRevenue),
    })),
});

const inflateCustomers = (customers: OrdersCustomersResponse): OrdersCustomersResponse => ({
    ...customers,
    ordersPerUser: inflateValue(customers.ordersPerUser),
    volumePerUser: inflateValue(customers.volumePerUser),
    businessRevenuePerUser: inflateValue(customers.businessRevenuePerUser),
});

const inflateToday = (today: OrdersTodayResponse): OrdersTodayResponse => ({
    ...today,
    volume: {
        ...today.volume,
        current: inflateValue(today.volume.current),
    },
    businessRevenue: {
        ...today.businessRevenue,
        current: inflateValue(today.businessRevenue.current),
    },
    hourlyVolume: today.hourlyVolume.map((point) => ({
        ...point,
        value: inflateValue(point.value),
    })),
});

const setList = (params: URLSearchParams, key: string, values: string[]) => {
    if (values.length > 0) {
        params.set(key, values.join(","));
    }
};

const buildParams = (
    filters: OrdersAnalyticsFilters,
    extras?: Record<string, string | number | undefined>
): URLSearchParams => {
    const params = new URLSearchParams();

    params.set("dateFrom", String(filters.dateFrom));
    params.set("dateTo", String(filters.dateTo));
    params.set("comparisonPeriod", filters.comparisonPeriod);
    params.set("granularity", filters.granularity);
    params.set("referral", filters.referral);
    params.set("userType", filters.userType);

    setList(params, "operationType", filters.operationType);
    setList(params, "status", filters.status);
    setList(params, "pair", filters.pair);
    setList(params, "destinationRail", filters.destinationRail);
    setList(params, "withdrawProvider", filters.withdrawProvider);
    setList(params, "depositType", filters.depositType);
    setList(params, "onrampProvider", filters.onrampProvider);
    setList(params, "country", filters.country);
    setList(params, "clients", filters.clients);
    setList(params, "userEmails", filters.userEmails ?? []);

    if (filters.problemsOnly === true) {
        params.set("problemsOnly", "true");
    }

    if (filters.cancelledOnly === true) {
        params.set("cancelledOnly", "true");
    }

    if (filters.excludeCancelled === true) {
        params.set("excludeCancelled", "true");
    }

    if (typeof filters.amountMin === "number") {
        params.set("amountMin", String(filters.amountMin));
    }

    if (typeof filters.amountMax === "number") {
        params.set("amountMax", String(filters.amountMax));
    }

    Object.entries(extras ?? {}).forEach(([key, value]) => {
        if (value !== undefined) {
            params.set(key, String(value));
        }
    });

    return params;
};

async function requestJson<T>(path: string, params: URLSearchParams): Promise<T> {
    const query = params.toString();
    const suffix = query === "" ? "" : `?${query}`;
    return httpClient.getJson<T>(`${API_PATH}${path}${suffix}`);
}

const fetchOrdersFilterOptions = (filters: OrdersAnalyticsFilters) => {
    return requestJson<OrdersFilterOptionsResponse>("/filter-options", buildParams(filters));
};

const fetchOrdersDashboard = async (
    filters: OrdersAnalyticsFilters,
    dimension: SegmentationDimension
): Promise<OrdersDashboardResponse> => {
    const dashboard = await requestJson<OrdersDashboardResponse>(
        "/dashboard",
        buildParams(filters, { dimension, timezone: "Europe/Madrid" })
    );

    return {
        ...dashboard,
        summary: inflateSummary(dashboard.summary),
        trends: inflateTrends(dashboard.trends),
        today: inflateToday(dashboard.today),
        customers: inflateCustomers(dashboard.customers),
        monetization: inflateMonetization(dashboard.monetization),
        segmentation: inflateSegmentation(dashboard.segmentation),
    };
};

const fetchOrdersSegmentation = async (
    filters: OrdersAnalyticsFilters,
    dimension: SegmentationDimension
) => {
    const segmentation = await requestJson<OrdersSegmentationResponse>(
        "/segmentation",
        buildParams(filters, { dimension })
    );

    return inflateSegmentation(segmentation);
};

export const ORDERS_DRILLDOWN_DAY_PAGE_SIZE = 1000;

const ORDERS_EMAIL_SEARCH_LOOKBACK_MS = 365 * 24 * 60 * 60 * 1000;

const fetchOrdersDrilldown = (
    filters: OrdersAnalyticsFilters,
    dayStartMs: number,
    dayEndMs: number
) => {
    const dayScopedFilters: OrdersAnalyticsFilters = {
        ...filters,
        dateFrom: dayStartMs,
        dateTo: dayEndMs,
        excludeCancelled: true,
    };

    return requestJson<OrdersDrilldownResponse>(
        "/",
        buildParams(dayScopedFilters, { page: 1, pageSize: ORDERS_DRILLDOWN_DAY_PAGE_SIZE })
    );
};

const fetchOrdersByEmail = (filters: OrdersAnalyticsFilters, userEmail: string) => {
    const now = Date.now();
    const emailScopedFilters: OrdersAnalyticsFilters = {
        ...filters,
        dateFrom: now - ORDERS_EMAIL_SEARCH_LOOKBACK_MS,
        dateTo: now,
        userEmails: [userEmail],
    };

    return requestJson<OrdersDrilldownResponse>(
        "/",
        buildParams(emailScopedFilters, { page: 1, pageSize: ORDERS_DRILLDOWN_DAY_PAGE_SIZE })
    );
};

export const ORDERS_PROBLEMS_PAGE_SIZE = 25;

const fetchProblemOrders = (
    filters: OrdersAnalyticsFilters,
    page: number,
    pageSize: number = ORDERS_PROBLEMS_PAGE_SIZE
) => {
    const now = Date.now();
    const problemsScopedFilters: OrdersAnalyticsFilters = {
        ...filters,
        dateFrom: now - ORDERS_EMAIL_SEARCH_LOOKBACK_MS,
        dateTo: now,
        problemsOnly: true,
        excludeCancelled: true,
    };

    return requestJson<OrdersDrilldownResponse>(
        "/",
        buildParams(problemsScopedFilters, { page, pageSize })
    );
};

export const ORDERS_CANCELLED_PAGE_SIZE = 25;

const fetchCancelledOrders = (
    filters: OrdersAnalyticsFilters,
    page: number,
    pageSize: number = ORDERS_CANCELLED_PAGE_SIZE
) => {
    const now = Date.now();
    const cancelledScopedFilters: OrdersAnalyticsFilters = {
        ...filters,
        dateFrom: now - ORDERS_EMAIL_SEARCH_LOOKBACK_MS,
        dateTo: now,
        cancelledOnly: true,
    };

    return requestJson<OrdersDrilldownResponse>(
        "/",
        buildParams(cancelledScopedFilters, { page, pageSize })
    );
};

const fetchSegmentationByDimensions = async (
    filters: OrdersAnalyticsFilters,
    dimensions: SegmentationDimension[]
): Promise<Partial<Record<SegmentationDimension, OrdersSegmentationResponse>>> => {
    const settled = await Promise.allSettled(
        dimensions.map((dimension) => fetchOrdersSegmentation(filters, dimension))
    );

    return dimensions.reduce<Partial<Record<SegmentationDimension, OrdersSegmentationResponse>>>(
        (result, dimension, index) => {
            const outcome = settled[index];
            if (outcome.status === "fulfilled") {
                result[dimension] = outcome.value;
            }
            return result;
        },
        {}
    );
};

const fetchCustomerIntelligence = (filters: OrdersAnalyticsFilters) => {
    return requestJson<CustomerIntelligenceResponse>(
        "/customer-intelligence",
        buildParams(filters)
    );
};

const fetchContactsIntelligence = (filters: OrdersAnalyticsFilters) => {
    return requestJson<ContactsIntelligenceResponse>(
        "/contacts-intelligence",
        buildParams(filters)
    );
};

const fetchSenderProfile = (filters: OrdersAnalyticsFilters) => {
    return requestJson<SenderProfileResponse>("/sender-profile", buildParams(filters));
};

const fetchEntityIntelligence = (filters: OrdersAnalyticsFilters) => {
    return requestJson<EntityIntelligenceResponse>("/entity-intelligence", buildParams(filters));
};

const fetchOrderById = (operationId: string) => {
    return requestJson<OrderDetail>(`/by-id/${encodeURIComponent(operationId)}`, new URLSearchParams());
};

async function downloadOrdersExportCsv(filters: OrdersAnalyticsFilters): Promise<void> {
    const params = buildParams(filters);
    const blob = await httpClient.getBlob(`${API_PATH}/export?${params.toString()}`);

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "orders-analytics-export.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export type AudienceSegment = { type: AudienceSegmentType; value: string };

// Fetches and hashes the audience but does NOT download — the UI decides when to
// save, so generating and downloading stay separate (no auto-download surprise).
async function prepareAudienceCsv(
    filters: OrdersAnalyticsFilters,
    segment: AudienceSegment
): Promise<{ blob: Blob; count: number }> {
    const params = buildParams(filters, {
        audienceSegmentType: segment.type,
        audienceSegmentValue: segment.value === "" ? undefined : segment.value,
    });
    const blob = await httpClient.getBlob(`${API_PATH}/export/audience?${params.toString()}`);

    // Row count = non-empty lines minus the header row.
    const text = await blob.text();
    const count = Math.max(0, text.split("\n").filter((line) => line !== "").length - 1);

    return { blob, count };
}

// Saves an already-prepared blob to disk — no network call, so re-downloading
// never re-generates the audience.
function saveCsvBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName.endsWith(".csv") ? fileName : `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export const ordersAnalyticsApi = {
    fetchOrdersDashboard,
    fetchOrdersFilterOptions,
    fetchOrdersSegmentation,
    fetchSegmentationByDimensions,
    fetchOrdersDrilldown,
    fetchOrdersByEmail,
    fetchProblemOrders,
    fetchCancelledOrders,
    fetchCustomerIntelligence,
    fetchContactsIntelligence,
    fetchSenderProfile,
    fetchEntityIntelligence,
    fetchOrderById,
    downloadOrdersExportCsv,
    prepareAudienceCsv,
    saveCsvBlob,
};
